import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header, Footer } from '../Common';
import HeroBanner from '@ajat/design-system/ContentBlocks/HeroBanner';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import USP from '@ajat/design-system/ContentBlocks/USP';
import ContentBanner from '@ajat/design-system/ContentBlocks/ContentBanner';
import useWidget from '../../../../hooks/useWidget';
import useShopCardPrices from '../../../../hooks/useShopCardPrices';
import { applyShopPrices } from '../../../../services/shopCardPrices';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useMenuItemListing from '../../../../hooks/useMenuItemListing';
import useMenuItemNavigation from '../../../../hooks/useMenuItemNavigation';
import { mapProductCarouselItems, mapMenuItemListingCards } from '../../../../hooks/widgetMappers';
import '../../Common/Landing/Landing.css';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-FI Landing Page — same structure and behaviour as the CLS-DK landing
 * (2026-10-14 alignment): showcase cards from the menu-item collections, the
 * visitor's shop prices on every card, anonymous card clicks open the read-only
 * product page. No Trustpilot (no FI business unit yet) and no demo cards —
 * a block with nothing to show is not rendered.
 * FI-TODO: hero photo pair, "Vaatteet" banner photo, copy marked below.
 * ══════════════════════════════════════════════════════════════════════════ */
const Landing = () => {
  const navigate = useNavigate();

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  /* CMS widgets — the fallback when a menu item has no collection. */
  const { data: collectionWidget } = useWidget('clsfi', 'homepage-products');
  const { data: packagesWidget } = useWidget('clsfi', 'homepage-packages');

  /* Landing cards + CTAs resolve into the customer's own school shop: the shop
   * mapped to the section's menu item → the clicked product/package when that
   * shop sells it → else the shop page → school without that shop type →
   * any open shop of the school; no open shop → the closed page; not
   * identified → the school popup (shop preselected), then the same rules. */
  const { menuItems, showSchoolSelector, openShopEnabled, landingProductsLimit } = useBrandSettings('clsfi');
  // anonToProductPage: without a school a card opens the product page (read-only), no popup.
  const onMenuItemClick = useMenuItemNavigation({ brandSlug: 'clsfi', feSlug: 'cls-fi', anonToProductPage: true });
  const toShop = (menuKey) => () => onMenuItemClick(menuKey, { toSchoolShop: true });
  const menu1Slug = (menuItems || []).find((m) => m.key === 'menu_1')?.slug;
  const menu2Slug = (menuItems || []).find((m) => m.key === 'menu_2')?.slug;
  const menu1Listing = useMenuItemListing('clsfi', menu1Slug, { showcase: true });
  const menu2Listing = useMenuItemListing('clsfi', menu2Slug, { showcase: true });
  const cardOptions = { feSlug: 'cls-fi', navigate, showSchoolSelector, openShopEnabled, onMenuItemClick, toSchoolShop: true, packageDescription: 'Pakettiratkaisu' };
  const menu1Cards = mapMenuItemListingCards(menu1Listing, { ...cardOptions, menuKey: 'menu_1', only: 'products' });
  const menu2Cards = mapMenuItemListingCards(menu2Listing, { ...cardOptions, menuKey: 'menu_2', only: 'products' });
  // Packages belong to the menu_1 collection; legacy setups keep them in the
  // menu_2 collection — still shown, and still carried to the menu_1 shop.
  const menu1Packages  = mapMenuItemListingCards(menu1Listing, { ...cardOptions, menuKey: 'menu_1', only: 'packages' });
  const packageCards   = menu1Packages.length
    ? menu1Packages
    : mapMenuItemListingCards(menu2Listing, { ...cardOptions, menuKey: 'menu_1', only: 'packages' });

  /* A visitor who has chosen a school sees THEIR shop's prices on every card — class and
   * shop overrides included — and only what that shop sells (services/shopCardPrices);
   * a grid with nothing left is not rendered. Anonymous visitors: the cards as they are.
   * While the shop's prices load the cards show without a price (no default-price flash). */
  const shopPrices = useShopCardPrices('clsfi');
  // Each block shows at most the brand's configured number of cards (Brands form →
  // "Front page: products per block") — cut AFTER the shop filter, so cards the
  // visitor's shop does not sell never take a slot.
  const forVisitor = (cards) => {
    const limited = (list) => list.slice(0, landingProductsLimit);
    if (!shopPrices.identified) return limited(cards);
    if (shopPrices.loading) return limited(cards).map((c) => ({ ...c, price: undefined }));
    return limited(applyShopPrices(cards, shopPrices, { feSlug: 'cls-fi' }));
  };
  const widgetOptions = { navigate, productBasePath: '/clothing/cls-fi/product-details', packageBasePath: '/clothing/cls-fi/product-package', schoolStorageKey: 'clsfi_school_selection', menuKey: 'menu_1', onMenuItemClick, toSchoolShop: true };
  const shown = {
    menu1Cards: forVisitor(
      (menu1Cards.length ? menu1Cards : null)
        ?? mapProductCarouselItems(collectionWidget, widgetOptions)
        ?? []),
    menu2Cards: forVisitor(menu2Cards),
    packageCards: forVisitor(
      (packageCards.length ? packageCards : null)
        ?? mapProductCarouselItems(packagesWidget, widgetOptions)
        ?? []),
  };

  return (
    <div className="landing">

      {/* ── TopBar + Header ── */}
      <Header />

      {/* ── Hero Banner — FI keeps its pre-alignment hero (HeroBanner v1: text panel +
          cutout visual); DK's full-bleed HeroBannerV2 needs a photo pair FI does not
          have (FI-TODO), and the cutout PNG made the white copy unreadable. ── */}
      <HeroBanner
        subtitle="PARAS HINTATAKUU"
        title="Vaatteita yhteenkuuluvuuden lisäämiseksi"
        copy="20 vuoden kokemuksellamme voimme luoda korkealaatuisia tuotteita riippumatta siitä, oletko koulu, oppilaskunta vai yhdistys!"
        ctaLabel="Osta koulutuotteita"
        ctaVariant="alternative"
        ctaOnClick={toShop('menu_1')}
        image={require('../../../../assets/abc-se/landing/abc-hero-visual.png')}
        imageAlt="Luokkaretkivaatteet"
        showCaption={false}
      />

      {/* ── USP Bar — full width like FI's pre-alignment page (DK constrains it to the
          layout width); no Trustpilot on FI yet — FI-TODO business unit ── */}
      <USP
        items={[
          { icon: 'fast-delivery', label: 'Nopea toimitus' },
          { icon: 'size-guarantee', label: '100% kokotakuu' },
          { icon: 'payment', label: 'Helppo yksilöllinen maksu' },
        ]}
      />

      {/* ── Products Widget 1: menu_1 collection ── */}
      {shown.menu1Cards.length > 0 && (
        <ProductsGrid
          moreLabel="lisää"
          background="var(--color-gray-0)"
          title="Mallisto"
          subtitle="Kaikki malliston vaatteet on huolellisesti testattu täyttämään korkeat laatuvaatimuksemme, pesu pesun jälkeen."
          ctaLabel="Katso koko mallisto"
          ctaVariant="black"
          ctaIcon={<ArrowRight size={20} />}
          ctaOnClick={toShop('menu_1')}
          items={shown.menu1Cards}
        />
      )}

      {/* ── menu_2 Banner (FI-TODO: photo + copy) ── */}
      <ContentBanner
        direction="left"
        align="center"
        subtitle="Vaatteet"
        title="Tilaa vaatteet"
        titleVariant="h1"
        titleAs="h2"
        theme='dark'
        showBullets={false}
        showParagraph={false}
        contentBg='#34020C'
        slogan="Yhteneväiset vaatteet koko luokalle"
        linkLabel="Katso vaatteet"
        onLinkClick={(e) => {
          e.preventDefault();
          onMenuItemClick('menu_2', { toSchoolShop: true });
        }}
        showCaption={false}
        image={require('../../../../assets/cls-fi/landing/banner-1.jpg')}
        imageAlt="Vaatteet"
      />

      {/* ── Products Widget 2: menu_2 collection ── */}
      {shown.menu2Cards.length > 0 && (
        <ProductsGrid
          moreLabel="lisää"
          background="var(--color-gray-0)"
          columns={{ base: 1, sm: 2, md: 3 }}
          ctaIcon={<ArrowRight size={20} />}
          ctaOnClick={toShop('menu_2')}
          items={shown.menu2Cards}
        />
      )}

      {/* ── Fullwidth Content Widget 1 ── */}
      <Fullwidth
        showImage={false}
        subtitle="YLI 75 000 ASIAKASTA VUOSITTAIN"
        mainCopyVariant="h2"
        mainCopy="Tarjoamme nopean toimituksen ja houkuttelevia pakettiratkaisuja, ja voit tilata turvallisesti yli 100 vuoden kokemuksella."
        additionalCopy="Helppo yksilöllinen maksu. Vapaa tuotevalinta."
        tags={['A4-selkäpainatus', '10 x 10 cm rintalogo', '2 painoväriä per luokka']}
        showHyperlink={false}
      />

      {/* ── Secondary Banner (Packages) ── */}
      <ContentBanner
        direction="right"
        align="center"
        subtitle="Alennukset jopa 20 %"
        title="Houkuttelevat pakettiratkaisut"
        titleVariant="h1"
        titleAs="h2"
        contentBg='#EBE6E0'
        showBullets={false}
        showParagraph={false}
        slogan="Säästä jopa 20 % pakettiratkaisuista"
        linkLabel="Katso pakettiratkaisut"
        onLinkClick={(e) => {
          e.preventDefault();
          onMenuItemClick('menu_1', { toSchoolShop: true });
        }}
        showCaption={false}
        images={[
          { src: require('../../../../assets/cls-fi/landing/banner-1.jpg'), alt: 'Pakettiratkaisu' },
          { src: require('../../../../assets/cls-fi/landing/banner-2.jpg'), alt: 'Hoodie & Sweatpants' },
          { src: require('../../../../assets/cls-fi/landing/banner-3.jpg'), alt: 'Hoodie & T-shirt' },
        ]}
        sliderInterval={5}
      />

      {/* ── Products Widget 3: Packages ── */}
      {shown.packageCards.length > 0 && (
        <ProductsGrid
          moreLabel="lisää"
          background="var(--color-gray-0)"
          columns={{ base: 1, sm: 2, md: 3 }}
          ctaOnClick={toShop('menu_1')}
          items={shown.packageCards}
        />
      )}

      {/* ── Fullwidth Content Widget 2 (with image) — FI-TODO: final copy ── */}
      <Fullwidth
        subtitle="KESTÄVYYS JA YHTEISÖLLISYYS"
        mainCopyVariant="h2"
        mainCopy="C.L. SEIFERT haluaa vaalia ympäristöä"
        additionalCopy="Meille on tärkeää, että tuotanto on kestävää, ja pyrimme valmistamaan tuotteemme jopa 100 % luomupuuvillasta. Kestävä kehitys on yrityksemme ydinarvo."
        image={require('../../../../assets/cls-fi/landing/about.jpg')}
        tags={['Nopea toimitus', 'Edulliset hinnat', 'Henkilökohtainen palvelu']}
        showHyperlink={false}
        hyperlinkLabel="Lue lisää"
        hyperlinkOnClick={(e) => {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('open-school-selector'));
        }}
      />

      {/* ── Footer ── */}
      <Footer />
    </div>
  );
};

export default Landing;
